import { defineReactiveModel, Persistable, hasMany } from "vue-app-utils";
import { formatDistanceToNow } from "date-fns";
import Model from "@/models/model";
import { recalcScorecardsForTournament } from "@/utils/recalc-scorecards-for-tournament";
import { generateMatchups } from "@/utils/generate-matchups";
import { applyLateArrivalGrace, computeNumMissedMatches } from "@/utils/match-count";

const Tournament = {
  extends: Model,
  mixins: [
    Persistable,
    hasMany("players", {
      orderBy: ["createdAt"]
    }),
    hasMany("matches", {
      orderBy: [["createdAt", "desc"]]
    })
  ],
  data () {
    return {
      numberOfTables: 2,
      winsToAdvanceGroup: 2.2,
      useMaxTotalGames: false,
      unpaidPlayers: 0,
      scoringLock: false,
      finished: false,
      callMatchesAutomatically: true,
      ratingsSyncedAt: null
    };
  },
  computed: {
    title () {
      return `Created ${formatDistanceToNow(this.createdAt.toDate())} ago.`;
    },
    name () {
      return formatDistanceToNow(this.createdAt.toDate());
    },
    activeMatches () {
      return this.matches.filter(m => m.active);
    },
    inactiveMatches () {
      return this.matches.filter(m => !m.active);
    },
    playerRatings () {
      return recalcScorecardsForTournament(this);
    },
    maxTotalMatches () {
      const presentPlayers = this.players.filter(p => p.present);
      return presentPlayers.length === 0 ? 0 : Math.max(...presentPlayers.map(p => p.totalMatches));
    },
    playerModelsReady () {
      return this.players.every(p => !!p.userModel?.scorecard);
    },
    canGenerateNewMatchup () {
      if (!this.playerModelsReady) return false;
      const result = generateMatchups(this);
      return result.matches.length > 0;
    }
  },
  methods: {
    async awaitPlayerModels () {
      this.players;
      await this.promisePlayers;
      await Promise.all(this.players.map(p => p.awaitUserModelAndScorecard()));
    },
    async numMissedMatchesForNewPlayer () {
      this.players;
      await this.promisePlayers;
      return applyLateArrivalGrace(computeNumMissedMatches(this.players));
    },
    async generateNextRound () {
      await this.awaitPlayerModels();
      const { teams, matches } = generateMatchups(this);
      const MatchModel = this.getModel("match");
      await Promise.all(
        [...matches].reverse().map(async (matchup) => {
          const matchId = await this.createMatch({
            tableNumber: matchup.tableNumber,
            active: true
          });
          const docRef = this.dbFns.doc(this.db, "matches", matchId);
          const match = new MatchModel({
            propsData: {
              docRef,
              user: this.user,
              parent: this,
              startSnapshot: false,
              docType: "match",
              tournament: this
            }
          });
          await Promise.all(
            matchup.teamIndices.map(teamIndex => {
              const playerIds = teams[teamIndex].map(p => p.id);
              return match.createTeam({ playerIds });
            })
          );
        })
      );
    }
  }
};

export default defineReactiveModel(Tournament);